import { SealCheckIcon } from '@phosphor-icons/react';
import React from 'react';

import { MediaSlot } from './MediaSlot';
import { MissingCreativeBand } from './MissingCreativeBand';
import {
  PlatformChevronRightIcon,
  PlatformIgBookmarkIcon,
  PlatformIgHeartIcon,
  PlatformIgMsgBubbleIcon,
  PlatformIgShareIcon,
  PlatformMoreHorizIcon,
} from './PlatformIcons';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  compactNumber,
  platformNaturalMaxWidth,
  resolveAspectRatio,
  resolveCarouselAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Instagram SPONSORED feed ad — the paid sibling of `InstagramPost`, and the
 * Instagram counterpart to `MetaAdPreview`/`LinkedInAdPreview`.
 *
 * Source: Figma file `Dl0gE7lH27N7x7OJb0DFBw` (the file key changed after a
 * Pro-team duplication — node ids are unchanged from the original
 * `V3dtfYTYqYBAJKrai804jY`), `instagram-post` frame (`28:12356`). All four
 * variants were pulled and pixel-compared against `get_screenshot` on
 * 2026-08-20: single/light `28:12355`, single/dark `28:12352`,
 * carousel/light `28:12354`, carousel/dark `28:12353`. This corrects an
 * earlier version of this file that was built with zero nodes pulled (the
 * Figma MCP was quota-blocked at the time) — see git history for that
 * version's reasoning and what it got wrong.
 *
 * Corrected against the real frames (see the handoff report for the full
 * diff list):
 *   - The CTA row has NO destination-domain or headline text in any
 *     variant — it is "Shop Now" (link in light / filled band in dark) on
 *     the left and a chevron-right glyph on the right, full stop. The prior
 *     reconstruction invented a Meta-style domain+headline block here.
 *   - The CTA row carries NO border on either edge — the divider sits
 *     between the CTA row and the action row instead, not between the media
 *     and the CTA row.
 *   - The header shows the account's display name as-is ("Club Doggo",
 *     title case, unmodified) — not a lowercased/stripped @handle.
 *   - The caption is a bare paragraph with no bold handle prefix — unlike
 *     `InstagramPost`'s organic caption, the ad template never repeats the
 *     handle before the caption text.
 *   - There is no like-count line in the template itself (it has no bound
 *     data state at all); this component still renders one when real
 *     `engagement` is supplied — see `__tests__/engagementTruth.test.tsx`,
 *     which is a cross-platform contract, not something this template can
 *     override.
 *   - Carousel slide-position dots render INSIDE the CTA row, centered
 *     between the CTA label and the chevron — not as a separate row above
 *     or below it.
 *   - Media is closer to Instagram's 4:5 portrait crop (the pulled frame is
 *     338×400, ≈0.845, well off Facebook's 1.91:1 and closer to 4:5's 0.8
 *     than to square) than to a 1:1 default.
 *   - "..." header glyph and the CTA chevron now render from the real
 *     Figma-sourced `PlatformMoreHorizIcon`/`PlatformChevronRightIcon`
 *     rather than Phosphor stand-ins, matching the icon convention this
 *     directory otherwise already follows (see `PlatformIcons.tsx`'s header
 *     comment on why Phosphor is not used for these glyphs).
 *
 * What the earlier reconstruction got RIGHT without ever seeing the frames:
 *   - The action row is heart / comment / paper-plane-share on the LEFT with
 *     bookmark pushed to the far right — Instagram's own icon set and
 *     layout, matching `InstagramPost.tsx`'s row (glyphs + order), not
 *     Facebook's like/comment/share triple.
 *   - `platform-tokens.ts`'s `ctaBackground`/`ctaText` roles for Instagram —
 *     light = bare link (no button fill), dark = filled blue band — are
 *     exactly what the frames show. No change needed.
 *   - "Sponsored" correctly replaces the organic timestamp, and there is no
 *     reaction/comment/share COUNT row for an unpublished ad.
 *   - Structural order (header → media → CTA → divider → action row →
 *     caption) was already right; only the caption's internal composition
 *     (dropping the handle prefix) needed a fix.
 *
 * The OUTER card previously carried a `border` (no shadow) — neither Figma
 * frame (`28:12355`/`28:12352`) has one; the root is a plain `rounded-[8px]`
 * fill with no border/stroke property at all, same as `MetaAdPreview`'s
 * root. It now carries `shadow-elevation-1` and nothing else, matching
 * `ARTIFACT_PREVIEW_CARD` (`src/lib/ui-tokens.ts`) — the treatment
 * `PostBody`'s own preview pane uses, and what `MetaAdPreview`/
 * `LinkedInAdPreview` already carried (this file was the outlier: border,
 * no shadow, where its siblings had shadow, and a border Figma never
 * specified). Internal dividers (the `border-t` between the CTA/action row
 * and the caption) are structural, per Figma, and stay.
 *
 * Deliberately NOT a `PlatformPreviewKind`, for the same reason
 * `MetaAdPreview`/`LinkedInAdPreview` are not: the dispatcher maps a stored
 * `posts.platforms` value to a shell, and an ad unit never carries one.
 */
export interface InstagramAdPreviewProps {
  brandName: string;
  brandColor?: string;
  avatarUrl?: string;
  isVerified?: boolean;
  /** The caption. Folds at Instagram's own feed length, same as `InstagramPost`. */
  caption: string;
  /**
   * NOT rendered by this template — verified against all four Figma frames,
   * none of which carry a headline node in the CTA row (it is "Shop Now" +
   * chevron only). Kept on the props contract for parity with
   * `MetaAdPreviewProps.headline` and for a caller that wants it available
   * for e.g. an aria-label, but it has no visual slot on the Instagram ad
   * anatomy. Do not add one back without a design change to point to.
   */
  headline?: string;
  /** The CTA button/link label ("Shop Now", "Learn More"). */
  cta: string;
  /**
   * NOT rendered by this template — same as `headline` above: verified
   * against all four Figma frames, none of which show a destination domain
   * in the CTA row. Kept for props parity only.
   */
  destinationDomain?: string | null;
  imageUrl?: string | null;
  /** Multiple images — the carousel variant (`28:12354`/`28:12353`). */
  imageUrls?: string[];
  imageAspectRatios?: Array<number | string | null>;
  mediaItems?: Array<{ url: string; kind: 'image' | 'video'; videoPoster?: string | null }>;
  carouselActiveIndex?: number;
  onCarouselActiveIndexChange?: (index: number) => void;
  videoUrl?: string | null;
  videoPoster?: string | null;
  /**
   * Defaults to 4:5 — the pulled Figma frame is 338×400 (≈0.845), a
   * portrait crop well off Facebook's 1.91:1 landscape default and closer
   * to Instagram's own 4:5 (0.8) feed-portrait maximum than to square.
   */
  aspectRatio?: number | string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /** Same contract as `MetaAdPreviewProps.expectedFilename` — omitted, never approximated. */
  expectedFilename?: string | null;
  /**
   * Real engagement only. Missing ⇒ hidden entirely (draft ad). Present ⇒
   * rendered as-is, including zero (a published ad with synced metrics).
   * Never invented — see `__tests__/engagementTruth.test.tsx`.
   */
  engagement?: { likes?: number };
  /** Same seam as `MetaAdPreviewProps.overlay`. */
  overlay?: React.ReactNode;
  /** Same contract as `MetaAdPreviewProps.embedded`. */
  embedded?: boolean;
  /**
   * Forces which Instagram skin variant renders, independent of Mora's own
   * active theme. Same seam `MetaAdPreview` exposes — the style guide needs
   * to show BOTH the light and dark Instagram variant side by side
   * regardless of which theme Mora itself is in (see platform-tokens.ts's
   * "HOW A CONSUMER APPLIES A SKIN" note). Omitted ⇒ follows Mora's theme.
   */
  theme?: 'light' | 'dark';
  className?: string;
}

// Instagram's real feed fold is commonly cited around 125 characters — same
// constant `InstagramPost.tsx` uses, kept in sync so an ad's caption folds at
// the identical point its organic sibling would.
const INSTAGRAM_AD_FOLD_LENGTH = 125;

export const InstagramAdPreview: React.FC<InstagramAdPreviewProps> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  isVerified = false,
  caption,
  headline,
  cta,
  destinationDomain,
  imageUrl,
  imageUrls,
  imageAspectRatios,
  mediaItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  videoUrl,
  videoPoster,
  aspectRatio,
  isLoading = false,
  emptyHint = 'No creative yet — copy only',
  expectedFilename,
  engagement,
  overlay,
  embedded = false,
  theme: themeOverride,
  className,
}) => {
  // Which skin variant renders is a per-instance JS choice, not an ambient
  // CSS cascade — see platform-tokens.ts's header comment for why. Mora's
  // OWN active theme selects which Instagram skin shows, per the goal doc's
  // §2 ruling, unless a caller (the style guide) forces a specific variant.
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = themeOverride ?? (isDarkMode ? 'dark' : 'light');
  const theme = PLATFORM_SKINS.instagram[resolvedTheme];

  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(
    caption,
    INSTAGRAM_AD_FOLD_LENGTH,
  );

  const { currentImage, currentVideoUrl, currentVideoPoster, carouselOverlay, slides, safeActive } =
    useCarousel(imageUrls, imageUrl, {
      activeIndex: carouselActiveIndex,
      onActiveIndexChange: onCarouselActiveIndexChange,
      mediaItems,
    });
  const resolvedAspect = resolveAspectRatio(
    resolveCarouselAspectRatio(aspectRatio, imageAspectRatios, safeActive),
    4 / 5,
  );
  const hasMedia = Boolean(currentImage || currentVideoUrl || videoUrl) || isLoading;
  const ctaLabel = cta.trim();
  const likes = engagement?.likes;
  // destinationDomain intentionally unused in JSX — see the props doc
  // comment: no destination-domain node exists in any of the four Figma
  // frames. (headline is still used below, as the media's alt text.)
  void destinationDomain;

  return (
    <div
      className={cn(embedded ? 'w-full' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${platformNaturalMaxWidth('instagram-post')}px` }}
    >
      <article
        className="w-full overflow-hidden rounded-xl shadow-elevation-1"
        style={{ backgroundColor: theme.cardBackground }}
        data-platform="instagram-ad"
      >
        {/*
          size-9 (36px) avatar, brandName rendered AS-IS (not a lowercased
          @handle) — the frames show the account's display name, "Club
          Doggo", title case and unmodified.
        */}
        <header className="flex items-center gap-3 px-4 py-3">
          <PreviewAvatar
            brandName={brandName}
            brandColor={brandColor}
            avatarUrl={avatarUrl}
            className="size-9 rounded-full"
            textClassName="text-[12px] font-semibold tracking-tight"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <p
                className="truncate text-[13px] font-semibold"
                style={{ color: theme.primaryText }}
              >
                {brandName}
              </p>
              {isVerified ? (
                <SealCheckIcon
                  className="size-3.5 shrink-0"
                  weight="fill"
                  style={{ color: theme.linkText ?? theme.primaryText }}
                  aria-label="Verified"
                />
              ) : null}
            </div>
            {/* "Sponsored" replaces the organic timestamp — same rule
                MetaAdPreview/LinkedInAdPreview follow: an unpublished ad has
                no real publication time to show. */}
            <p className="text-[12px]" style={{ color: theme.secondaryText }}>
              Sponsored
            </p>
          </div>
          <PlatformMoreHorizIcon
            size={18}
            className="shrink-0"
            style={{ color: theme.primaryText }}
          />
        </header>

        {hasMedia ? (
          <MediaSlot
            ratio={resolvedAspect}
            bg="muted"
            imageUrl={currentImage}
            videoUrl={currentVideoUrl ?? videoUrl}
            videoPoster={currentVideoPoster ?? videoPoster}
            isLoading={isLoading}
            emptyHint={emptyHint}
            alt={headline || `${brandName} ad creative`}
            overlay={
              overlay ? (
                <>
                  {carouselOverlay}
                  {overlay}
                </>
              ) : (
                carouselOverlay
              )
            }
          />
        ) : (
          <MissingCreativeBand
            hint={emptyHint}
            expectedFilename={expectedFilename}
            aspectRatio={4 / 5}
          />
        )}

        {/*
          CTA row — the genuinely Instagram-shaped piece, and the biggest
          structural correction from the earlier reconstruction: all four
          frames show ONLY the CTA label (left) and a chevron (right), with
          no domain/headline block and no border on either edge of this row
          — the divider sits below it instead (on the action-row container).
          `theme.ctaBackground`/`theme.ctaText` already encode the real
          per-theme difference: light has no button fill (a bare link on the
          card), dark is a filled band — see platform-tokens.ts's Instagram
          section for the sourced values. Carousel slide dots render
          centered INSIDE this row, between the label and the chevron —
          exactly where the carousel frames place them, not in a row of
          their own.
        */}
        <div
          className="relative flex h-10 items-center justify-between pl-4 pr-4"
          style={{ backgroundColor: theme.ctaBackground }}
        >
          <span className="shrink-0 text-[13px] font-semibold" style={{ color: theme.ctaText }}>
            {ctaLabel || 'No CTA'}
          </span>
          {slides ? (
            <div
              className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1"
              aria-hidden
            >
              {slides.map((_, i) => (
                <span
                  key={i}
                  className="size-1.5 rounded-full transition-colors"
                  style={{
                    backgroundColor:
                      i === safeActive
                        ? (theme.linkText ?? theme.primaryText)
                        : theme.secondaryText,
                    opacity: i === safeActive ? 1 : 0.35,
                  }}
                />
              ))}
            </div>
          ) : null}
          <PlatformChevronRightIcon
            size={11}
            className="shrink-0"
            style={{ color: theme.linkText ?? theme.ctaText }}
          />
        </div>

        {/*
          Action row + caption share ONE bordered container in the source —
          the divider (border-t) belongs here, between the CTA row and the
          action row, not between the media and the CTA row.
        */}
        <div
          className="flex flex-col items-end gap-4 border-t px-4 pb-6 pt-3"
          style={{ backgroundColor: theme.cardBackground, borderColor: theme.cardBorder }}
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-4">
              <PlatformIgHeartIcon size={18} style={{ color: theme.primaryText }} />
              <PlatformIgMsgBubbleIcon size={18} style={{ color: theme.primaryText }} />
              <PlatformIgShareIcon size={18} style={{ color: theme.primaryText }} />
            </div>
            <PlatformIgBookmarkIcon size={18} style={{ color: theme.primaryText }} />
          </div>

          {/*
            No like-count node exists in the template itself — this line
            only appears when real `engagement` is supplied (published,
            synced ad). See `__tests__/engagementTruth.test.tsx`, which
            requires the same "hidden when absent, rendered including zero
            when present" contract across every replica in this directory.
          */}
          {likes != null ? (
            <p className="w-full text-[13px] font-semibold" style={{ color: theme.primaryText }}>
              {compactNumber(likes)} likes
            </p>
          ) : null}

          {/*
            Bare paragraph, no bold handle prefix — unlike `InstagramPost`'s
            organic caption, none of the four ad frames repeat the account
            name before the caption text.
          */}
          {caption ? (
            <p
              className="w-full whitespace-pre-wrap break-words text-[13px] leading-relaxed"
              style={{ color: theme.primaryText }}
            >
              {visible}
              {needsFold && !expanded ? (
                <>
                  {'… '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="opacity-70 transition-opacity hover:opacity-100"
                    style={{ color: theme.secondaryText }}
                  >
                    more
                  </button>
                </>
              ) : null}
            </p>
          ) : null}
        </div>
      </article>
    </div>
  );
};
